import * as THREE from 'three';
import { randomInt } from '../utils/helpers';
import { LANE_WIDTH, OBSTACLE_COLORS, OBSTACLE_DIMENSIONS, AABB } from './constants';

export type ObstacleType = 'block' | 'spike' | 'wall' | 'low';

/**
 * Obstacle that the player must avoid
 */
export class Obstacle {
  public mesh: THREE.Mesh;
  public type: ObstacleType;
  public lane: number;
  public passed = false;

  constructor(type: ObstacleType, lane: number, zPosition: number) {
    this.type = type;
    this.lane = lane;
    this.mesh = this.createMesh(type);
    this.mesh.position.set(
      (lane - 1) * LANE_WIDTH,
      this.getBaseY(type),
      zPosition
    );
  }

  // ===========================================================================
  // Public Methods
  // ===========================================================================

  public update(speed: number, deltaTime: number): void {
    this.mesh.position.z -= speed * deltaTime;
  }

  public isOffScreen(): boolean {
    return this.mesh.position.z < -5;
  }

  public getBoundingBox(): AABB {
    const pos = this.mesh.position;
    const dims = this.getDimensions();

    return {
      minX: pos.x - dims.halfWidth,
      maxX: pos.x + dims.halfWidth,
      minY: pos.y - dims.halfHeight,
      maxY: pos.y + dims.halfHeight,
      minZ: pos.z - dims.halfDepth,
      maxZ: pos.z + dims.halfDepth,
    };
  }

  public static createRandom(zPosition: number): Obstacle {
    const types: ObstacleType[] = ['block', 'spike', 'wall', 'low'];
    const type = types[randomInt(0, types.length - 1)];
    const lane = randomInt(0, 2);
    return new Obstacle(type, lane, zPosition);
  }

  // ===========================================================================
  // Private Methods
  // ===========================================================================

  private createMesh(type: ObstacleType): THREE.Mesh {
    const geometry = this.createGeometry(type);
    const material = new THREE.MeshLambertMaterial({
      color: OBSTACLE_COLORS[type],
      flatShading: true,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    return mesh;
  }

  private createGeometry(type: ObstacleType): THREE.BufferGeometry {
    switch (type) {
      case 'block': {
        const d = OBSTACLE_DIMENSIONS.block;
        return new THREE.BoxGeometry(d.width, d.height, d.depth);
      }
      case 'spike': {
        const d = OBSTACLE_DIMENSIONS.spike;
        return new THREE.ConeGeometry(d.radius, d.height, d.segments);
      }
      case 'wall': {
        const d = OBSTACLE_DIMENSIONS.wall;
        return new THREE.BoxGeometry(d.width, d.height, d.depth);
      }
      case 'low': {
        const d = OBSTACLE_DIMENSIONS.low;
        return new THREE.BoxGeometry(d.width, d.height, d.depth);
      }
      default:
        return new THREE.BoxGeometry(1, 1, 1);
    }
  }

  private getBaseY(type: ObstacleType): number {
    return OBSTACLE_DIMENSIONS[type].baseY;
  }

  private getDimensions(): { halfWidth: number; halfHeight: number; halfDepth: number } {
    switch (this.type) {
      case 'block':
        return { halfWidth: 0.6, halfHeight: 0.6, halfDepth: 0.6 };
      case 'spike':
        return { halfWidth: 0.4, halfHeight: 0.75, halfDepth: 0.4 };
      case 'wall':
        return { halfWidth: 1.1, halfHeight: 1.25, halfDepth: 0.2 };
      case 'low':
        return { halfWidth: 0.75, halfHeight: 0.3, halfDepth: 0.75 };
      default:
        return { halfWidth: 0.5, halfHeight: 0.5, halfDepth: 0.5 };
    }
  }
}
